/* ==========================================================================
   WD Footer v2
   1. Accordion behaviour for the footer link columns (mobile only).
   2. Native newsletter signup -> Klaviyo webhook, with the EN/FR preference.
   3. Hides the signup for logged-in customers who are already subscribed.
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 740px)';
  var mql = window.matchMedia(MOBILE_QUERY);
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ *
   * 1. Accordions
   * ------------------------------------------------------------------ */

  function panelFor(toggle) {
    var id = toggle.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function transitionMs(element) {
    var declared = getComputedStyle(element).transitionDuration || '0s';
    var first = declared.split(',')[0].trim();
    var value = parseFloat(first);

    if (isNaN(value)) return 0;
    return first.indexOf('ms') > -1 ? value : value * 1000;
  }

  // transitionend alone is not dependable — a background tab, an off-screen
  // footer or a cancelled transition can swallow it, which would leave a closed
  // panel out of [hidden] and its links in the tab order. Always back it with a
  // timer and run the callback exactly once.
  function afterTransition(element, callback) {
    var duration = transitionMs(element);

    if (!duration) {
      callback();
      return;
    }

    var settled = false;
    var timer;

    var finish = function () {
      if (settled) return;
      settled = true;
      element.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      callback();
    };

    var onEnd = function (event) {
      if (event.target !== element || event.propertyName !== 'max-height') return;
      finish();
    };

    element.addEventListener('transitionend', onEnd);
    timer = setTimeout(finish, duration + 60);
  }

  function openPanel(panel, animate) {
    panel.hidden = false;

    if (!animate) {
      panel.style.maxHeight = 'none';
      return;
    }

    panel.style.maxHeight = panel.scrollHeight + 'px';

    afterTransition(panel, function () {
      // Release the cap so the panel keeps fitting if its content reflows.
      if (panel.dataset.state === 'open') panel.style.maxHeight = 'none';
    });
  }

  function closePanel(panel, animate) {
    if (!animate) {
      panel.style.maxHeight = '0px';
      panel.hidden = true;
      return;
    }

    // Go from an explicit height so the transition has something to animate from.
    panel.style.maxHeight = panel.scrollHeight + 'px';
    // Force a reflow before dropping to 0.
    void panel.offsetHeight;
    panel.style.maxHeight = '0px';

    afterTransition(panel, function () {
      if (panel.dataset.state === 'closed') panel.hidden = true;
    });
  }

  function setExpanded(toggle, panel, expanded, animate) {
    toggle.setAttribute('aria-expanded', String(expanded));
    panel.dataset.state = expanded ? 'open' : 'closed';

    if (expanded) {
      openPanel(panel, animate);
    } else {
      closePanel(panel, animate);
    }
  }

  function applyViewport(footer) {
    var isMobile = mql.matches;
    var toggles = footer.querySelectorAll('[data-wd-footer2-toggle]');

    Array.prototype.forEach.call(toggles, function (toggle) {
      var panel = panelFor(toggle);
      if (!panel) return;

      if (isMobile) {
        toggle.removeAttribute('aria-disabled');
        toggle.removeAttribute('tabindex');

        // Restore what the shopper opened, not the forced-open desktop state.
        setExpanded(toggle, panel, toggle.dataset.userExpanded === 'true', false);
      } else {
        // Columns are permanently open above the breakpoint, so the heading is
        // no longer an operable control.
        toggle.setAttribute('aria-expanded', 'true');
        toggle.setAttribute('aria-disabled', 'true');
        toggle.setAttribute('tabindex', '-1');
        panel.dataset.state = 'open';
        panel.hidden = false;
        panel.style.maxHeight = '';
      }
    });
  }

  function initAccordions(footer) {
    var toggles = footer.querySelectorAll('[data-wd-footer2-toggle]');

    Array.prototype.forEach.call(toggles, function (toggle) {
      var panel = panelFor(toggle);
      if (!panel) return;

      toggle.dataset.userExpanded = 'false';

      toggle.addEventListener('click', function () {
        if (!mql.matches) return;

        var expanded = toggle.dataset.userExpanded === 'true';
        toggle.dataset.userExpanded = String(!expanded);
        setExpanded(toggle, panel, !expanded, !reduceMotion.matches);
      });
    });

    var wasMobile = mql.matches;
    applyViewport(footer);

    var onChange = function () {
      if (mql.matches === wasMobile) return;
      wasMobile = mql.matches;
      applyViewport(footer);
    };

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
    }

    // Belt and braces: some embedded/emulated viewports resize without ever
    // dispatching a matchMedia change event. onChange no-ops unless the
    // breakpoint actually flipped, so this stays cheap.
    var resizeTimer;
    window.addEventListener(
      'resize',
      function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(onChange, 100);
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------------ *
   * 2. Newsletter
   * ------------------------------------------------------------------ */

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KLAVIYO_REVISION = '2024-10-15';

  var STRINGS = {
    en: {
      invalid: 'Please enter a valid email address.',
      sending: 'Sending…',
      success: 'Thanks — you are on the list.',
      error: 'Something went wrong. Please try again.',
      submit: 'Submit',
      thanksTitle: 'Thank you!',
      thanksText: 'You’re in! Check your inbox for upcoming drops and special offers.',
      thanksCta: 'Shop new arrivals'
    },
    fr: {
      invalid: 'Veuillez saisir une adresse courriel valide.',
      sending: 'Envoi en cours…',
      success: 'Merci — vous êtes inscrit.',
      error: 'Une erreur est survenue. Veuillez réessayer.',
      submit: 'Soumettre',
      thanksTitle: 'Merci !',
      thanksText: 'C’est fait ! Surveillez votre boîte de réception pour les nouveautés et offres exclusives.',
      thanksCta: 'Voir les nouveautés'
    }
  };

  function pageLocale() {
    var lang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
    return lang.indexOf('fr') === 0 ? 'fr' : 'en';
  }

  function showThanks(form, t, language) {
    // Replace the signup (email + language + button) with a confirmation card.
    var signup = form.closest('.wd-footer2__signup') || form.parentNode;
    if (!signup) return;

    var shopUrl = form.dataset.shopUrl || (language === 'fr' ? '/fr/collections/all' : '/collections/all');

    var card = document.createElement('div');
    card.className = 'wd-footer2__thanks';
    card.setAttribute('role', 'status');
    card.setAttribute('aria-live', 'polite');

    var title = document.createElement('p');
    title.className = 'wd-footer2__thanks-title';
    title.textContent = t.thanksTitle;

    var text = document.createElement('p');
    text.className = 'wd-footer2__thanks-text';
    text.textContent = t.thanksText;

    var cta = document.createElement('a');
    cta.className = 'wd-footer2__thanks-cta';
    cta.href = shopUrl;
    cta.textContent = t.thanksCta;

    card.appendChild(title);
    card.appendChild(text);
    card.appendChild(cta);

    signup.innerHTML = '';
    signup.appendChild(card);
  }

  function subscribeToKlaviyo(form, email, language, name) {
    var companyId = form.dataset.companyId;
    var listId = form.dataset.listId;
    if (!companyId || !listId) return Promise.reject(new Error('Klaviyo settings are incomplete'));

    var profile = {
      email: email,
      first_name: name || undefined,
      properties: {
        language: language,
        source: form.dataset.source || 'Footer signup'
      },
      subscriptions: {
        email: { marketing: { consent: 'SUBSCRIBED' } }
      }
    };

    var timezone = '';
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      /* Some older browsers do not expose the timezone. */
    }
    if (timezone) profile.location = { timezone: timezone };

    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: form.dataset.source || 'Footer signup',
              profile: { data: { type: 'profile', attributes: profile } }
            },
            relationships: { list: { data: { type: 'list', id: listId } } }
          }
        })
      }
    ).then(function (response) {
      if (response.status === 202 || response.ok) return;
      return response.text().then(function (text) {
        throw new Error(text || ('HTTP ' + response.status));
      });
    });
  }

  function browserTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
  }

  function initNewsletter(form) {
    var message = form.querySelector('[data-wd-newsletter-message]');
    var submit = form.querySelector('.wd-footer2__submit');
    var input = form.querySelector('input[name="email"]');
    var endpoint = form.dataset.endpoint;
    var t = STRINGS[pageLocale()];

    if (!endpoint || !input) return;

    var say = function (text, state) {
      if (!message) return;
      message.textContent = text || '';
      if (state) {
        message.setAttribute('data-state', state);
      } else {
        message.removeAttribute('data-state');
      }
    };

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      var email = input.value.trim();

      if (!EMAIL_RE.test(email)) {
        form.classList.add('is-invalid');
        say(t.invalid, 'error');
        input.focus();
        return;
      }

      form.classList.remove('is-invalid');

      var checked = form.querySelector('input[name="language"]:checked');
      var language = checked ? checked.value : pageLocale();
      var nameField = form.querySelector('input[name="name"]');

      var body = new FormData();
      body.append('email', email);
      body.append('language', language);
      body.append('source', form.dataset.source || 'Footer signup');
      if (browserTimezone()) body.append('timezone', browserTimezone());
      if (form.dataset.listId) body.append('list_id', form.dataset.listId);
      if (nameField && nameField.value) body.append('name', nameField.value);

      var originalLabel = submit ? submit.textContent : '';
      if (submit) {
        submit.disabled = true;
        submit.textContent = t.sending;
      }
      say('');

      // The webhook owns the server-side profile enrichment. Use no-cors so
      // the browser does not reject a successful cross-origin submission when
      // the response does not expose CORS headers.
      var klaviyoRequest = fetch(endpoint, { method: 'POST', mode: 'no-cors', body: body });

      klaviyoRequest
        .then(function () {
          try {
            sessionStorage.setItem('klaviyo_sub_status_' + email, 'true');
          } catch (e) {
            /* private mode — nothing to do */
          }

          // Swap the whole signup out for a confirmation card.
          showThanks(form, t, language);
        })
        .catch(function (error) {
          console.error('[wd-footer-v2] newsletter signup failed:', error);
          say(t.error, 'error');
          if (submit) submit.textContent = originalLabel;
        })
        .then(function () {
          if (submit) submit.disabled = false;
        });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3. Hide the signup for customers who already subscribed
   * ------------------------------------------------------------------ */

  function hideSignup(footer) {
    var promo = footer.querySelector('.wd-footer2__promo');
    if (promo) promo.style.display = 'none';
  }

  function initSubscribedCheck(footer) {
    var config = window.wdFooterCustomer;
    if (!config || !config.email || !config.statusEndpoint) return;

    var key = 'klaviyo_sub_status_' + config.email;
    var cached = null;

    try {
      cached = sessionStorage.getItem(key);
    } catch (e) {
      /* private mode */
    }

    if (cached === 'true') {
      hideSignup(footer);
      return;
    }

    if (cached === 'false') return;

    var separator = config.statusEndpoint.indexOf('?') === -1 ? '?' : '&';
    var url = config.statusEndpoint + separator + 'email=' + encodeURIComponent(config.email);

    fetch(url, { cache: 'no-cache' })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var subscribed = !!(data && data.exists);

        try {
          sessionStorage.setItem(key, String(subscribed));
        } catch (e) {
          /* private mode */
        }

        if (subscribed) hideSignup(footer);
      })
      .catch(function (error) {
        console.error('[wd-footer-v2] subscription check failed:', error);
      });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    var footers = document.querySelectorAll('[data-wd-footer2]');

    Array.prototype.forEach.call(footers, function (footer) {
      if (footer.dataset.wdFooter2Ready === 'true') return;
      footer.dataset.wdFooter2Ready = 'true';

      initAccordions(footer);
      initSubscribedCheck(footer);

      var form = footer.querySelector('[data-wd-newsletter]');
      if (form) initNewsletter(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-wd-footer2]')) init();
  });
})();
